<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BtnSyncInvoice',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'success']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const beforeDestroy = (...args) => {
    {
      {
        invoiceSyncPoller.value && invoiceSyncPoller.value.stop();
      }
    }
  };
  const checkStatus = (...args) => {
    if (!jobData.value) {
      console.log('JobData is null, stop polling.');
      invoiceSyncPoller.value.stop();
      return;
    }

    const status = jobData.value.status;

    if (status !== 'pending' && status !== 'running') {
      console.log(
        `JobStatus is ${status}, stop polling.`,
        invoiceSyncPoller.value
      );
      invoiceSyncPoller.value.stop();
      emit('success');
      return;
    }

    $fn.value
      .fetch('read_a_job', { parameters: { id: jobData.value.id } })
      .then((data) => (jobData.value = data))
      .catch((err) => {
        console.error(err);
        invoiceSyncPoller.value.stop();
      });
  };

  onBeforeUnmount(beforeDestroy);

  const jobData = ref(null);
  const invoiceSyncPoller = ref(null);
  const messages = ref({
    createButtonText: $fn.value.pgettext('BtnSyncInvoice', 'Issue Invoice'),
    createButtonTitle: $fn.value.pgettext(
      'BtnSyncInvoice',
      'Issuance of the invoice by the billing service provider'
    ),
    syncButtonTooltip: $fn.value.pgettext('BtnSyncInvoice', 'Synchronization'),
    confirmSyncDialog: {
      header: $fn.value.pgettext('BtnSyncInvoice', 'Synchronize invoice'),
      message: $fn.value.pgettext(
        'BtnSyncInvoice',
        'The invoice can  only be uploaded once in the szamlazz.hu system, after uploading the invoice cannot be modified'
      ),
      btnOk: $fn.value.pgettext('BtnSyncInvoice', 'Synchronization'),
      btnCancel: $fn.value.pgettext('BtnSyncInvoice', 'Cancel'),
    },
    cancelSyncDialog: {
      header: $fn.value.pgettext('BtnSyncInvoice', 'Cancel synchronization'),
      message: $fn.value.pgettext(
        'BtnSyncInvoice',
        'If interrupted, the invoice will not be synchronized. Its content can be edited and re-synchronized with the new data afterwards. Queue processing is paused at the interrupted item.'
      ),
      btnOk: $fn.value.pgettext('BtnSyncInvoice', 'Interrupt'),
      btnCancel: $fn.value.pgettext('BtnSyncInvoice', 'Cancel'),
    },
    showLogMessagesTooltip: $fn.value.pgettext(
      'BtnSyncInvoice',
      'View log file'
    ),
  });
  const statuses = ref({
    default: {
      variant: 'primary',
      text: $fn.value.pgettext('BtnSyncInvoice', 'Issue Invoice'),
      tooltip: $fn.value.pgettext(
        'BtnSyncInvoice',
        'Issuance of the invoice by the billing service provider'
      ),
    },
    pending: {
      icon: 'material-symbols:lock-clock-outline-sharp',
      color: 'bg-yellow-200',
      variant: 'warning',
      text: $fn.value.pgettext(
        'BtnSyncInvoice',
        'Invoice issuance in progress'
      ),
      tooltip: $fn.value.pgettext(
        'BtnSyncInvoice',
        'The invoice is waiting for synchronization and cannot be modified in this state.'
      ),
    },
    retry: {
      icon: 'material-symbols:lock-clock-outline-sharp',
      color: 'bg-yellow-200',
      variant: 'warning',
      text: $fn.value.pgettext(
        'BtnSyncInvoice',
        'Invoice issuance in progress'
      ),
      tooltip: $fn.value.pgettext(
        'BtnSyncInvoice',
        'The invoice is being synchronized.'
      ),
    },
    processing: {
      icon: 'material-symbols:cloud-sync-rounded',
      color: 'bg-blue-200',
      variant: 'warning',
      text: $fn.value.pgettext(
        'BtnSyncInvoice',
        'Invoice issuance in progress'
      ),
      tooltip: $fn.value.pgettext(
        'BtnSyncInvoice',
        'The invoice is being synchronized.'
      ),
    },
    failed: {
      icon: 'material-symbols:sync-problem-outline-rounded',
      color: 'bg-red-200',
      variant: 'danger',
      text: $fn.value.pgettext('BtnSyncInvoice', 'Invoice issuance error'),
      tooltip: $fn.value.pgettext(
        'BtnSyncInvoice',
        'An error occurred during synchronization.'
      ),
    },
    cancelled: {
      icon: 'material-symbols:sync-disabled',
      color: 'bg-gray-200',
      variant: 'secondary',
      text: $fn.value.pgettext('BtnSyncInvoice', 'Invoice issuance cancelled'),
      tooltip: $fn.value.pgettext(
        'BtnSyncInvoice',
        'The invoice was removed from the synchronization queue.'
      ),
    },
  });
  const ux = ref([]);

  const $data = computed(() => ({
    jobData: jobData.value,
    invoiceSyncPoller: invoiceSyncPoller.value,
    messages: messages.value,
    statuses: statuses.value,
    ux: ux.value,
  }));

  const $$load_invoiceSyncPoller = () => {
    return $fn.value.poller({ id: 'InvoiceSync', callback: checkStatus });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'jobData' },
      { name: 'invoiceSyncPoller' },
      { name: 'messages' },
      { name: 'statuses' },
      { name: 'ux' },
    ],
    loaders: { invoiceSyncPoller: $$load_invoiceSyncPoller },
    ux: {},
    dataRefs: { jobData, invoiceSyncPoller, messages, statuses, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const status = computed(() => {
    if (loaded.value) {
      return jobData.value
        ? jobData.value.status
        : props.invoice.sync_job
          ? props.invoice.sync_job.status
          : null;
    }
    return null;
  });
  const context = computed(() => {
    if (loaded.value) {
      return statuses.value[status.value || 'default'];
    }
    return null;
  });
  const isJobRunning = computed(() => {
    return status.value === 'running' || status.value === 'pending';
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  const act_2248226175642056_setData_ukd2v2n5z_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'jobData',
      value: $event.data.modalResult && $event.data.modalResult.value,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_2248226175642056_close_fe7yj9ay2_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_2248226175642056_script_lhe7rq4ae_value_callable = async (
    $event
  ) => {
    return invoiceSyncPoller.value.start();
  };
  const act_2248226175642056_script_lhe7rq4ae_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_lhe7rq4ae_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_close_uo53tt3wy_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_kpzvyxs4u_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ConfirmSyncInvoice',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_5962862420439144_showModal_70j9wje4i_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: props.invoice.sync_job.id,
      },
    };
  };

  const act_2248226175642056_setData_ukd2v2n5z = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_ukd2v2n5z_getActionArgs,
  });
  const inst_evh_2248226175642056_close_fe7yj9ay2 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_ukd2v2n5z,
      event: 'close',
      when: inst_evh_2248226175642056_close_fe7yj9ay2_getWhen,
      displayName: 'setData',
    });
  const act_2248226175642056_script_lhe7rq4ae = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_lhe7rq4ae_getActionArgs,
  });
  const inst_evh_2248226175642056_close_uo53tt3wy =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_lhe7rq4ae,
      event: 'close',
      when: inst_evh_2248226175642056_close_uo53tt3wy_getWhen,
      displayName: 'script',
    });
  const act_2248226175642056_showModal_kpzvyxs4u =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_kpzvyxs4u_getActionArgs,
      displayName: 'sync',
      events: [
        inst_evh_2248226175642056_close_fe7yj9ay2,
        inst_evh_2248226175642056_close_uo53tt3wy,
      ],
    });
  const inst_evh_2248226175642056_clickPreventStop_x7dqdw5k5 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_kpzvyxs4u,
      event: 'click.prevent.stop',
      displayName: 'sync',
    });
  const inst_evh_2248226175642056_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_clickPreventStop_x7dqdw5k5],
      event: 'click.prevent.stop',
    });
  const act_5962862420439144_showModal_70j9wje4i =
    new actionTypes.ShowModalAction({
      actionArgs: act_5962862420439144_showModal_70j9wje4i_getActionArgs,
      displayName: 'showJob',
    });
  const inst_evh_5962862420439144_clickPreventStop_hlosqb9zg =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_showModal_70j9wje4i,
      event: 'click.prevent.stop',
      displayName: 'showJob',
    });
  const inst_evh_5962862420439144_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_clickPreventStop_hlosqb9zg],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_2248226175642056_close_fe7yj9ay2,
    inst_evh_2248226175642056_close_uo53tt3wy,
    inst_evh_2248226175642056_clickPreventStop_x7dqdw5k5,
    sync: inst_evh_2248226175642056_clickPreventStop_x7dqdw5k5,
    inst_evh_2248226175642056_clickPreventStop,
    inst_evh_5962862420439144_clickPreventStop_hlosqb9zg,
    showJob: inst_evh_5962862420439144_clickPreventStop_hlosqb9zg,
    inst_evh_5962862420439144_clickPreventStop,
  };

  const evh_2248226175642056_close_fe7yj9ay2 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_fe7yj9ay2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_uo53tt3wy = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_uo53tt3wy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_clickPreventStop_x7dqdw5k5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_clickPreventStop_x7dqdw5k5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPreventStop_hlosqb9zg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_clickPreventStop_hlosqb9zg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="row">
    <ItpButton
      icon="material-symbols:cloud-sync-outline-rounded"
      :text="context.text"
      :tooltip="context.tooltip"
      :spinning="actions.sync.isRunning || isJobRunning"
      :disabled="actions.sync.isRunning || isJobRunning"
      :variant="context.variant"
      v-if="invoice.meta.billing_service_status.status != 'disabled' && context"
      @click.prevent.stop="evh_2248226175642056_clickPreventStop($event, {})"
    >
    </ItpButton>
    <ItpButton
      icon="icon-park-outline:log"
      :tooltip="messages.showLogMessagesTooltip"
      variant="light"
      size="sm"
      v-if="invoice.sync_job && invoice.sync_job.id"
      @click.prevent.stop="evh_5962862420439144_clickPreventStop($event, {})"
    >
    </ItpButton>
  </ItpBox>
</template>
